import { handleUpdateSchedule } from "../../../redux/specialist-slices/scheduleSlots.slice";
import { hideDialog, refresher } from "../../../redux/guest-slices/other.slice";
import { useSelector, useDispatch } from "react-redux";
import Icons from "../../../assets/js/Icons";
import { useState, useEffect, useRef } from "react";

import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import DurationSelector from "../ScheduleTimings/durationSelector";

const EditTimeSlotDialog = () => {
  const dispatch = useDispatch();
  const durationRef = useRef("");
  const { data } = useSelector((state) => state.otherSlice);
  const {
    selectedDay,
    userTimeZone,
    initialSlots = [],
    bookedSlots = new Set(),
    slotDuration = 30,
  } = data || {};
  const [timeRanges, setTimeRanges] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (initialSlots.length > 0) {
      const formattedSlots = initialSlots.map((slot) => ({
        ...slot,
        startTime: slot.startTime,
        endTime: slot.endTime,
      }));

      const ranges = consolidateSlotsToRanges(formattedSlots);
      setTimeRanges(
        ranges.length > 0
          ? ranges
          : [{ id: Date.now().toString(), startTime: "", endTime: "" }]
      );
    } else {
      setTimeRanges([
        { id: Date.now().toString(), startTime: "", endTime: "" },
      ]);
    }
    setErrorMessage("");
  }, [initialSlots]);

  const consolidateSlotsToRanges = (slots) => {
    if (!slots.length) return [];
    const sorted = [...slots].sort((a, b) =>
      a.startTime.localeCompare(b.startTime)
    );
    const ranges = [];
    let current = { ...sorted[0] };

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startTime === current.endTime) {
        current.endTime = sorted[i].endTime;
      } else {
        ranges.push({ ...current });
        current = { ...sorted[i] };
      }
    }
    ranges.push(current);
    return ranges.map((r) => ({
      id: Date.now() + Math.random(),
      startTime: r.startTime,
      endTime: r.endTime,
    }));
  };

  const divideTimeRange = (startTime, endTime, duration) => {
    const slots = [];

    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);

    let current = sh * 60 + sm;
    const end = eh * 60 + em;

    while (current + duration <= end) {
      const slotStart = `${String(Math.floor(current / 60)).padStart(
        2,
        "0"
      )}:${String(current % 60).padStart(2, "0")}`;
      current += duration;
      const slotEnd = `${String(Math.floor(current / 60)).padStart(
        2,
        "0"
      )}:${String(current % 60).padStart(2, "0")}`;

      slots.push({
        id: `${Date.now()}_${slotStart}`,
        startTime: slotStart,
        endTime: slotEnd,
      });
    }

    return slots;
  };

  const handleRangeChange = (id, field, value) => {
    setTimeRanges((prev) =>
      prev.map((range) =>
        range.id === id ? { ...range, [field]: value } : range
      )
    );
  };

  const handleAddRange = () => {
    setTimeRanges((prev) => [
      ...prev,
      { id: Date.now().toString(), startTime: "", endTime: "" },
    ]);
  };

  const handleRemoveRange = (id) => {
    setTimeRanges((prev) => prev.filter((range) => range.id !== id));
  };

  const isOverlapping = (range1, range2) => {
    return (
      range1.startTime < range2.endTime && range2.startTime < range1.endTime
    );
  };

  const checkForBookedAppointments = (dividedSlots) => {
    const bookedInRange = [];
    dividedSlots.forEach((slot) => {
      const slotKey = `${selectedDay}_${slot.startTime}`;
      if (bookedSlots.has(slotKey)) {
        bookedInRange.push(slot.startTime);
      }
    });
    return bookedInRange;
  };

  const handleSave = async () => {
    for (let range of timeRanges) {
      if (!range.startTime || !range.endTime) {
        setErrorMessage("Please fill all time fields");
        return;
      }
      if (range.startTime >= range.endTime) {
        setErrorMessage("End time must be after start time");
        return;
      }
    }

    for (let i = 0; i < timeRanges.length; i++) {
      for (let j = i + 1; j < timeRanges.length; j++) {
        if (isOverlapping(timeRanges[i], timeRanges[j])) {
          setErrorMessage("Time ranges cannot overlap");
          return;
        }
      }
    }

    const allDividedSlots = [];
    timeRanges.forEach((range) => {
      const divided = divideTimeRange(
        range.startTime,
        range.endTime,
        durationRef.current.value.value
      );
      allDividedSlots.push(...divided);
    });

    const bookedTimes = checkForBookedAppointments(allDividedSlots);
    if (bookedTimes.length > 0) {
      setErrorMessage(
        `Warning: You have appointments booked for the following times: ${bookedTimes.join(
          ", "
        )}. ` +
        `You must cancel all appointments for these timings before updating the schedule.`
      );
      return;
    }

    setErrorMessage("");

    try {
      setIsLoading(true);
      const response = await dispatch(
        handleUpdateSchedule({
          day: selectedDay,
          slots: allDividedSlots,
          slotDuration: durationRef.current.value.value,
        })
      ).unwrap();

      if (response) {
        dispatch(refresher());
        dispatch(hideDialog());
      }
    } catch (error) {
      setErrorMessage(
        error?.error || "Failed to save schedule. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ py: 3, px: 4 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" fontWeight={700} color="other.color1">
            Add Time Slot
          </Typography>
          <IconButton onClick={() => dispatch(hideDialog())} size="large">
            <Icons.Cross />
          </IconButton>
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          All times in {userTimeZone?.toUpperCase()} • Slot Duration:{" "}
          {slotDuration} mins
        </Typography>
      </DialogTitle>


      <DialogContent sx={{ p: 4, pb: 2 }}>
        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setErrorMessage("")}
          >
            {errorMessage}
          </Alert>
        )}

        <DurationSelector
          durationRef={durationRef}
          slotDuration={slotDuration}
        />

        <Stack spacing={4} mt={2.5}>
          {timeRanges.map((range, index) => (
            <Box key={range.id}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Typography fontWeight={600} sx={{ mb: 2, fontSize: 15 }}>
                Time Range {index + 1}
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>
                    Start Time
                  </Typography>
                  <TextField
                    fullWidth
                    type="time"
                    value={range.startTime}
                    onChange={(e) =>
                      handleRangeChange(range.id, "startTime", e.target.value)
                    }
                    inputProps={{ step: 300 }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>
                    End Time
                  </Typography>
                  <TextField
                    fullWidth
                    type="time"
                    value={range.endTime}
                    onChange={(e) =>
                      handleRangeChange(range.id, "endTime", e.target.value)
                    }
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 2 }}>
                  {timeRanges.length > 1 && (
                    <IconButton
                      color="error"
                      sx={{ textTransform: "none" }}
                      size="large"
                      onClick={() => handleRemoveRange(range.id)}
                    >
                      <Icons.DeleteSolid />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </Box>
          ))}

          <Button
            startIcon={<Icons.PlusSolid />}
            variant="outlined"
            color="primary"
            onClick={handleAddRange}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            Add More
          </Button>
        </Stack>
      </DialogContent>

      <Box
        sx={{ px: 4, py: 3, borderTop: "1px solid", borderColor: "grey.200" }}
      >
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isLoading}
          size="large"
          sx={{
            py: 1.75,
            fontSize: 16,
            fontWeight: 600,
            borderRadius: 2,
            textTransform: "none",
          }}
        >
          {isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </>
  );
};

export default EditTimeSlotDialog;
